import React from 'react';
import { Box, FormControl, Grid, RadioGroup, TextField, Typography } from '@mui/material';

import { LevelRadioButton } from 'components';
import { useSupervisorSuspendServiceFormManager } from './supervisor-suspend-service-form.hook';

interface SupervisorSuspendServiceFormProps {
  zoneName?: string;
  buildingName?: string;
  orgSlug: string;
  buildingId: number;
  scheduleId: number;
  userId: number;
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const SupervisorSuspendServiceForm = React.forwardRef(
  (
    {
      onSubmitting = () => {},
      next,
      zoneName = '',
      buildingName = '',
      orgSlug,
      buildingId,
      scheduleId,
      userId,
    }: SupervisorSuspendServiceFormProps,
    ref,
  ) => {
    const [{ formManager, loading_submit }] = useSupervisorSuspendServiceFormManager({
      orgSlug,
      buildingId,
      scheduleId,
      userId,
      next,
    });

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    return (
      <Box sx={{ paddingY: 2 }}>
        <form onSubmit={formManager.handleSubmit} autoComplete="off">
          <Grid container sx={{ alignItems: 'center' }} spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <RadioGroup row name="payment_method" value={formManager.values.impactsBuilding ? 'building' : 'zone'}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <LevelRadioButton
                        value={'zone'}
                        active={!formManager.values.impactsBuilding}
                        title={'Zone Level'}
                        subtitle={zoneName}
                        description={
                          'Suspending the zone will temporarily turn off the scheduled service(s) for the remainder of the day.'
                        }
                        onClick={(value) => formManager.setFieldValue('impactsBuilding', value === 'building')}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <LevelRadioButton
                        value={'building'}
                        active={formManager.values.impactsBuilding}
                        title={'Building Level'}
                        subtitle={buildingName}
                        description={`Suspending all zones will temporarily turn off the scheduled service(s) for the remainder of the day.`}
                        onClick={(value) => formManager.setFieldValue('impactsBuilding', value === 'building')}
                      />
                    </Grid>
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Please add a comment why.
              </Typography>

              <Typography color="textSecondary" sx={{ fontSize: 12, marginY: 1 }}>
                All comments will be added to the Service Zone report.
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comment"
                placeholder="Add comment..."
                variant="outlined"
                value={formManager.values.comment}
                onChange={formManager.handleChange('comment')}
                error={!!(formManager.errors.comment && formManager.touched.comment)}
                helperText={(formManager.touched.comment ? formManager.errors.comment : '') as string}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    );
  },
);
